import { App as CapacitorApp } from '@capacitor/app'
import { Preferences } from '@capacitor/preferences'
import { toast } from 'sonner'
import type { EmailOtpType } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { setActiveCompanyId } from './company-context'

export const PENDING_COMPANY_KEY = 'pending_company_id'
export const MAGIC_LINK_PREFIX = 'pronteer://auth/callback'

/**
 * Complete a magic-link / OTP sign-in from the URL params Supabase appends to
 * the redirect target. Shared by the native deep-link handler (params from the
 * pronteer:// URL) and the web /auth/callback route (params from the browser
 * URL). Returns an error message on failure, or null on success.
 */
export async function handleAuthCallbackParams(
  params: URLSearchParams,
): Promise<{ error: string | null }> {
  const errorDesc = params.get('error_description')
  if (errorDesc) return { error: decodeURIComponent(errorDesc) }

  const tokenHash = params.get('token_hash')
  const type = params.get('type') as EmailOtpType | null
  const code = params.get('code')

  let signInError: { message?: string } | null = null
  if (tokenHash && type) {
    const { error } = await supabase.auth.verifyOtp({ type, token_hash: tokenHash })
    signInError = error
  } else if (code) {
    const { error } = await supabase.auth.exchangeCodeForSession(code)
    signInError = error
  } else {
    return { error: 'Sign-in link is missing a code. Try requesting a new link.' }
  }

  if (signInError) {
    return { error: signInError.message || 'This sign-in link is no longer valid' }
  }

  await applyPendingCompany(params.get('company_id'))
  return { error: null }
}

/**
 * Apply company selection after a successful sign-in: an explicit id from the
 * callback URL wins, else the PENDING_COMPANY_KEY persisted before the email
 * was sent. Shared by the link callback and the typed-OTP sign-in path.
 */
export async function applyPendingCompany(companyIdFromUrl?: string | null): Promise<void> {
  if (companyIdFromUrl) {
    await setActiveCompanyId(companyIdFromUrl)
    return
  }
  const { value: pending } = await Preferences.get({ key: PENDING_COMPANY_KEY })
  if (pending) {
    await setActiveCompanyId(pending)
    await Preferences.remove({ key: PENDING_COMPANY_KEY })
  }
}

export function registerAuthDeepLinkHandler() {
  CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
    if (!url.startsWith(MAGIC_LINK_PREFIX)) return

    const { error } = await handleAuthCallbackParams(new URL(url).searchParams)
    if (error) toast.error(error)
  })
}
